import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { RouterService } from 'app/global/router.service';
import { AuthFacade } from 'app/store/auth.facade';
import { Subject, take } from 'rxjs';
import { DocumentsService } from '../../documents/documents.service';
import { EditorEmitterService } from '../../events/editor-emitter.service';
import { StatusCodes } from 'app/auth/interceptors/status-codes';
import { ValidationHandlerService } from 'app/global/validation-handler/validation-handler.service';
import { DocumentTypeFieldNameEnum, DocumentTypeGroupEnum, IFieldTypeEnum } from '../../settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';

@Injectable({
    providedIn: 'root'
})

export class SelectTemplateService {
    public loadingSubject = new Subject<boolean>();
    public loading$ = this.loadingSubject.asObservable();
    public _isThirdPartyLogin$ = this._authFacade$.isThirdPartyLogin$;

    constructor(
        private editorEmitterService: EditorEmitterService,
        private documentsService: DocumentsService,
        private globalService: GlobalService,
        private modal: NgbModal,
        private router: Router,
        private _authFacade$: AuthFacade,
        private _routerService: RouterService,
        private validationHandler: ValidationHandlerService
    ) { }

    goToSendDocument(template, template_id: number, fromEditArea: boolean = false): void {
        this.editorEmitterService.setUnsavedChangesFlag(false);
        const data = structuredClone(template);
        this.loadingSubject.next(true);
        data['template']['template_id'] = template_id;
        data['template']['id'] = null;
        data['template_details']['video_type'] = 'youtube';
        const sectionsData = this.buildEntitySectionsPayload(data);
        data['top_section'] = sectionsData.top_section;
        data['signature'] = sectionsData.signature;
        data['details'] = sectionsData.details;
        delete data['detail'];
        data['fields_data'] = fromEditArea ? this.globalService?.fields_data : template?.template_fields_datas?.data
        this.documentsService.createDocument(data)
            .subscribe({
                next: async (document) => {
                    this.loadingSubject.next(false);
                    this.modal.dismissAll();
                    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                    this.redirectToSendDocumentArea('/send-document/', document.template.id, template.template.id);
                }, error: (error) => {
                    this.loadingSubject.next(false);
                    if (error?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
                        this.validationHandler.handleValidationErrors(error?.error, false);
                    } else {
                        this.globalService.toastrError(error);
                    }
                }
            });
    }

    
    
    private buildEntitySectionsPayload(template: any): { top_section: Record<string, any>, signature: Record<string, any>, details: Record<string, any> } {
        const payload = {
            top_section: this.hasValues(template?.top_section) ? template.top_section : {},
            signature: this.hasValues(template?.signature) ? template.signature : {},
            details: this.hasValues(template?.details)
                ? template.details
                : (this.hasValues(template?.detail) ? template.detail : {})
        };

        const groups = template?.layout_settings?.groups ?? [];
        groups.forEach((group) => {
            const groupName = group?.name;
            if (![DocumentTypeGroupEnum.TopSection, DocumentTypeGroupEnum.Signature, DocumentTypeGroupEnum.Details].includes(groupName)) {
                return;
            }

            const target = payload[groupName];
            if (this.hasValues(target)) {
                return;
            }

            (group?.fields ?? []).forEach((field) => {
                if (!field?.field_name) {
                    return;
                }
                if(groupName === DocumentTypeGroupEnum.TopSection && field.type === IFieldTypeEnum.INDEX_FIELD){
                    if (!target['fields'] || typeof target['fields'] !== 'object') {
                        target['fields'] = {};
                    }
                    target['fields'][field.field_name] = field.value ?? null;
                } else {
                    target[field.field_name] = field.value ?? null;
                }
                this.mapExtendedFieldValues(target, field);
            });
        });

        this.ensureTopSectionDefaults(payload.top_section);

        return payload;
    }

    
    
    private ensureTopSectionDefaults(topSection: Record<string, any>): void {
        if (!Object.prototype.hasOwnProperty.call(topSection, 'fields')) {
            topSection['fields'] = {};
        }
        if (!Object.prototype.hasOwnProperty.call(topSection, 'removed_category_ids')) {
            topSection['removed_category_ids'] = [];
        }
    }

    
    private mapExtendedFieldValues(target: Record<string, any>, field: any): void {
        if (field.field_name === DocumentTypeFieldNameEnum.SigningReminder) {
            target['recurring'] = field?.recurring ?? target['recurring'] ?? false;
            return;
        }

        if (field.field_name === DocumentTypeFieldNameEnum.DocumentKey) {
            target['dk_type'] = field?.dk_type ?? target['dk_type'] ?? 'password';
            target['dk_password'] = field?.dk_password ?? target['dk_password'] ?? '';
            target['dk_password_hint'] = field?.dk_password_hint ?? target['dk_password_hint'] ?? '';
            target['dk_send_via_sms'] = field?.dk_send_via_sms ?? target['dk_send_via_sms'] ?? 0;
            return;
        }

        if (field.field_name === DocumentTypeFieldNameEnum.ActiveBcc) {
            const existingBccSettings = target['bcc_settings'];
            if (existingBccSettings && typeof existingBccSettings === 'object') {
                return;
            }
            target['bcc_settings'] = field?.bcc_settings && typeof field.bcc_settings === 'object'
                ? field.bcc_settings
                : { subject: null, email: null };
        }
        if(field.field_name  === DocumentTypeFieldNameEnum.ExternalChat){
            target['external_chat'] = field.external_chat ?? 0;
        }
    }

    
    
    private hasValues(value: unknown): boolean {
        return !!value && typeof value === 'object' && Object.keys(value as object).length > 0;
    }



    redirectToSendDocumentArea(route: string, documentId: number, templateId: string) {
        return this._isThirdPartyLogin$.pipe(take(1)).subscribe(async (isThirdPartyLogin: boolean) => {
            const queryParams = await this._routerService.parseQueryParams();
            const url = `${route}${documentId}`;
            if (isThirdPartyLogin) {
                this.router.navigate([url], { queryParams });
            } else {
                this.router.navigate([url], { state: { template_id: templateId } });
            }
        });
    }
}
